import { defineStore } from 'pinia'
import type { IFullUserInfo } from '@/models/interfaces'
import { DateTime } from 'luxon'

/**
 * Lightweight CommonStore for the observer build.
 *
 * Uses the same Pinia store ID ('common') so that shared composables
 * like useDate can resolve useCommonStore() without pulling in the
 * parent app's heavy CommonStore and its dependency chain
 * (httpClient, WorkerService, IndexedDB workers, etc.).
 *
 * The observer Vite config aliases '@/stores/CommonStore' to this file.
 *
 * All defaults are self-contained here - no $patch from main.observer.ts
 * is needed. The timezone will eventually come from the professional
 * session / API response; for now it defaults to America/New_York.
 */
export const useCommonStore = defineStore('common', {
  state: () => ({
    fullUserInfo: {
      userId: 99001,
      caseId: 5001,
      firstName: 'Observer',
      lastName: 'User',
      fullName: 'Observer User',
      initials: 'OU',
      email: 'observer@talkingparents.com',
      timeZoneId: 1,
      timeZoneIdentifierString: 'America/New_York',
      timeZoneNameForMomentJs: 'America/New_York',
      dateFormatId: 1,
      shortDateFormat: 'MM/dd/yyyy',
      dateFormatName: 'MM/dd/yyyy',
      traceUser: false,
      coparentFirstName: 'Parent',
      coparentLastName: 'A',
      coparentFullName: 'Parent A',
      coparentInitials: 'PA'
    } as Partial<IFullUserInfo>,
    windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024
  }),

  getters: {
    getDateTimeSettings: (state) => ({
      shortDateFormat: state.fullUserInfo.shortDateFormat ?? '',
      timeZoneNameForMomentJs: state.fullUserInfo.timeZoneNameForMomentJs ?? ''
    }),

    isDesktopWidth: (state) => state.windowWidth >= 1025,
    isTabletWidth: (state) => state.windowWidth >= 768 && state.windowWidth < 1025,
    isMobileWidth: (state) => state.windowWidth < 768
  },

  actions: {
    formatDateInUserTz(
      date: DateTime | string | null | undefined,
      overrideFormat?: string
    ): string {
      if (!date) return ''
      const tz = this.fullUserInfo.timeZoneNameForMomentJs ?? 'utc'
      const fmt = overrideFormat ?? this.fullUserInfo.shortDateFormat ?? 'MM/dd/yyyy'

      if (date instanceof DateTime) {
        return date.setZone(tz).toFormat(fmt)
      }

      return DateTime.fromISO(date, { zone: 'utc' }).setZone(tz).toFormat(fmt)
    }
  }
})
